// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { db, auth } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { getCurrentUserCompanyId } from "../utils/userUtils";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import logo from "../assets/image/logo_web.png";

dayjs.extend(isBetween);

const COLORS = ["#4F46E5", "#22C55E", "#EF4444", "#F59E0B", "#0EA5E9"];

export default function Analytics() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formsPerMonth, setFormsPerMonth] = useState([]);
  const [submissionsData, setSubmissionsData] = useState([]);
  const [approvalStats, setApprovalStats] = useState({ approved: 0, rejected: 0 });
  const [userActivity, setUserActivity] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [range, setRange] = useState("30");
  const [insightData, setInsightData] = useState(null);

  // Translate insights reactively so language changes are reflected
  const insights = React.useMemo(() => {
    if (!insightData) return [];
    if (insightData.empty) return [t('analytics.noSubmissions')];
    return [
      `📈 ${t('analytics.insightChecksThisWeek', { total: insightData.checksThisWeek, trend: insightData.trend })}`,
      `❗ ${t('analytics.insightRejectionRate', { rate: insightData.rejectRate })}`,
      `👥 ${t('analytics.insightMostActiveUser', { user: insightData.activeUser })}`
    ];
  }, [insightData, t]);

  useEffect(() => {
    loadAnalytics();
  }, [range]);

  async function loadAnalytics() {
    const companyId = await getCurrentUserCompanyId();
    if (!companyId) return;

    // ----- FORMS CREATED PER MONTH -----
    const formsRef = collection(db, "companies", companyId, "forms");
    const formsSnap = await getDocs(formsRef);

    const monthCount = {};
    formsSnap.forEach((doc) => {
      const created = doc.data().createdAt?.toDate?.();
      if (!created) return;
      const key = dayjs(created).format("MMM YYYY");
      monthCount[key] = (monthCount[key] || 0) + 1;
    });

    const monthData = Object.entries(monthCount).map(([month, count]) => ({ month, count }));
    setFormsPerMonth(monthData);

    // ----- SUBMISSIONS -----
    const submissionsRef = collection(db, "companies", companyId, "submissions");
    const submissionsSnap = await getDocs(submissionsRef);

    const dailyCount = {};
    const userCounts = {};

    let approved = 0;
    let rejected = 0;

    // Fetch user data for display names
    const usersRef = collection(db, "users");
    const usersQuery = query(usersRef, where("companyId", "==", companyId));
    const usersSnap = await getDocs(usersQuery);
    const userMap = {};
    usersSnap.forEach(doc => {
      const userData = doc.data();
      userMap[doc.id] = userData.fullName || userData.email?.split('@')[0] || userData.email || doc.id;
    });

    const allSubmissions = [];

    submissionsSnap.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      
      // FILTER OUT DRAFTS FROM ANALYTICS
      if (data.status === "draft") return;
      
      allSubmissions.push(data);

      const date = data.createdAt?.toDate?.();
      if (!date) return;

      // Filter by range
      if (!dayjs(date).isAfter(dayjs().subtract(parseInt(range), "day"))) return;

      const key = dayjs(date).format("YYYY-MM-DD");
      dailyCount[key] = (dailyCount[key] || 0) + 1;

      // approvals
      if (data.approvalStatus === "approved") approved++;
      if (data.approvalStatus === "rejected") rejected++;

      // user leaderboard - use display name
      const userId = data.conductedBy || data.createdBy || "Unknown";
      const userName = userMap[userId] || userId;
      userCounts[userName] = (userCounts[userName] || 0) + 1;

      // user activity heatmap
      const day = date.getDate();
      userActivity[day] = (userActivity[day] || 0) + 1;
    });

    const dailyData = Object.entries(dailyCount).map(([date, count]) => ({ date, count }));
    setSubmissionsData(dailyData);
    setApprovalStats({ approved, rejected });
    setUserActivity(userActivity);
    
    const leaderboardData = Object.entries(userCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([user, count], i) => ({
        user,
        count,
        fill: COLORS[i % COLORS.length],
      }));
    
    setLeaderboard(leaderboardData);
    generateInsights(allSubmissions, leaderboardData);
  }

  function generateInsights(subs, topUsers) {
    if (!subs.length) {
      setInsightData({ empty: true });
      return;
    }

    const last7 = subs.filter(s => {
      const date = s.createdAt?.toDate?.();
      return date && dayjs(date).isAfter(dayjs().subtract(7, "day"));
    });

    const prev7 = subs.filter(s => {
      const date = s.createdAt?.toDate?.();
      return date && dayjs(date).isBetween(
        dayjs().subtract(14, "day"),
        dayjs().subtract(7, "day"),
        null,
        "[]"
      );
    });

    const trend = ((last7.length / (prev7.length || 1)) * 100 - 100).toFixed(1);
    const rejections = subs.filter(s => s.approvalStatus === "rejected").length;
    const rejectRate = ((rejections / subs.length) * 100).toFixed(1);

    const trendLabel = `${trend >= 0 ? '+' : ''}${trend}`;
    const activeUser = topUsers.length
      ? `${topUsers[0].user} (${topUsers[0].count})`
      : null;

    setInsightData({
      checksThisWeek: last7.length,
      trend: trendLabel,
      rejectRate,
      activeUser: activeUser || t('analytics.insightNoActivity', 'No activity recorded yet'),
    });
  }

  function exportCSV() {
    let csv = "Date,User,Form,Status\n";
    submissionsData.forEach(s => {
      csv += `${s.date},${s.user || "N/A"},${s.form || "N/A"},${s.status || "N/A"}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "analytics_export.csv";
    link.click();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-32 -top-24 w-80 h-80 bg-indigo-600/20 blur-3xl rounded-full" />
        <div className="absolute right-8 top-20 w-96 h-96 bg-emerald-300/12 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-10 w-96 h-96 bg-violet-500/12 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-10">

      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 print:mb-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="BuildMyChecks" className="w-12 h-12 object-contain" />
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-gray-400">{t('analytics.eyebrow', 'Insights')}</p>
            <h1 className="text-3xl font-bold text-white">{t('analytics.title')}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition">
            {t('common.back')}
          </button>
          <button 
            onClick={() => window.location.href = "https://buildmychecks.co.uk/home"}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition">
            {t('dashboardHeader.home')}
          </button>
          <button onClick={exportCSV}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow transition">
            {t('analytics.exportCsv')}
          </button>
          <button onClick={() => window.print()}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white shadow transition">
            {t('analytics.printPdf')}
          </button>
        </div>
      </div>

      {/* AI INSIGHTS */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3 text-white">{t('analytics.aiInsights')}</h2>
        <ul className="space-y-2 text-gray-300">
          {insights.map((insight, idx) => (
            <li key={idx} className="py-1">• {insight}</li>
          ))}
        </ul>
      </div>

      {/* DATE RANGE FILTER */}
      <div className="mb-6 print:hidden">
        <label className="mr-3 font-medium text-gray-200">{t('analytics.dateRange')}</label>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-100"
        >
          <option value="7">{t('analytics.last7Days')}</option>
          <option value="30">{t('analytics.last30Days')}</option>
          <option value="90">{t('analytics.last90Days')}</option>
          <option value="365">{t('analytics.last12Months')}</option>
        </select>
      </div>

      {/* CHARTS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* FORMS PER MONTH */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-5">
          <h3 className="text-lg font-semibold mb-3 text-white">{t('analytics.formsCreatedPerMonth')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={formsPerMonth}>
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* SUBMISSIONS PER DAY */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-5">
          <h3 className="text-lg font-semibold mb-3 text-white">{t('analytics.submissionsPerDay')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={submissionsData}>
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }} />
              <Line type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} dot={{ fill: '#0ea5e9', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* APPROVALS VS REJECTIONS */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-5">
          <h3 className="text-lg font-semibold mb-3 text-white">{t('analytics.approvalsVsRejections')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={[
                  { name: t('analytics.approved'), value: approvalStats.approved, fill: "#22C55E" },
                  { name: t('analytics.rejected'), value: approvalStats.rejected, fill: "#EF4444" },
                ]}
                dataKey="value"
                nameKey="name"
                label
                cx="50%"
                cy="50%"
                outerRadius={80}
              />
              <Legend wrapperStyle={{ color: '#d1d5db' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* USER ACTIVITY HEATMAP */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-5">
          <h3 className="text-lg font-semibold mb-3 text-white">{t('analytics.userActivityHeatmap')}</h3>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 31 }).map((_, i) => {
              const day = i + 1;
              const count = userActivity[day] || 0;
              return (
                <div
                  key={day}
                  title={`${t('analytics.dayLabel', 'Day')} ${day}: ${count}`}
                  className="w-8 h-8 text-xs flex items-center justify-center rounded"
                  style={{
                    backgroundColor: count ? `rgba(99, 102, 241, ${Math.min(0.3 + count * 0.15, 1)})` : 'rgba(255,255,255,0.05)',
                    color: count > 2 ? "#fff" : "#9ca3af",
                  }}
                >
                  {day}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* LEADERBOARD */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-6">
        <h2 className="text-lg font-semibold mb-4 text-white">{t('analytics.mostActiveUsers')}</h2>
        
        {leaderboard.length > 0 ? (
          <>
            <div className="mb-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={leaderboard.slice(0, 5)} layout="vertical">
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="user" type="category" width={120} stroke="#9ca3af" />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e5e7eb' }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {leaderboard.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2">
              {leaderboard.map((entry, i) => (
                <div key={entry.user} className="flex justify-between py-2 border-b border-white/10 last:border-none">
                  <span className="font-medium text-gray-100">{i + 1}. {entry.user}</span>
                  <span className="text-gray-400">{t('analytics.submissionsCount', { count: entry.count })}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-gray-400">{t('analytics.noUsersYet', 'No user activity recorded yet.')}</p>
        )}
      </div>

      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
